import { getParent, getRoot, types } from 'mobx-state-tree'
import { GameChoice } from './GameChoice'
import { isEmpty, isEqual, xorWith } from 'lodash'

export const GameQuestion = types
  .model('GameQuestion')
  .props({
    id: types.identifierNumber,
    text: types.string,
    image_url: types.maybe(types.string),
    alt_text: types.maybeNull(types.string),
    factoid: types.frozen(),
    choices: types.array(GameChoice),
    question_type: types.enumeration([
      'Ordering',
      'Open Text',
      'Matching',
      'Multiple Choice',
      'True or False',
      'Multiple Response',
    ]),
    answered: types.maybe(types.boolean),
    start_time: types.maybe(types.number),
    answer_time: types.maybe(types.number),
    showFeedback: types.maybe(types.boolean),
    userMatching: types.frozen(),
    userOrdering: types.frozen(),
    userText: types.maybe(types.string),
    case_sensitive: types.maybe(types.boolean),
    maxChoiceHeight: types.optional(types.number, 0),
  })
  .actions((self) => ({
    setMaxChoiceHeight(height) {
      if (height > self.maxChoiceHeight) {
        self.maxChoiceHeight = height
      }
    },
    setStartTime(time) {
      self.start_time = time
    },
    setAnswerTime(time) {
      self.answer_time = time
    },
    setShowFeedback(time) {
      self.showFeedback = time
    },
    reset() {
      self.answered = false
      self.choices.map((choice) => choice.reset())
    },
    setUserMatching(data) {
      self.userMatching = data
    },
    setUserOrdering(data) {
      self.userOrdering = data
    },
    setUserText(data) {
      self.userText = data
    },
  }))
  .views((self) => ({
    get TF() {
      return self.question_type === 'True or False'
    },
    get MC() {
      return self.question_type === 'Multiple Choice'
    },
    get MCA() {
      return self.question_type === 'Multiple Response'
    },
    get selected() {
      return self.choices.filter((c) => c.selected)
    },
    get leftMatching(): any {
      return self.choices[0]?.pairs?.map((pair) => pair.left)
    },
    get rightMatching(): any {
      return self.choices[0]?.pairs?.map((pair) => pair.right)
    },
    get pairs(): any {
      return self.choices[0]?.pairs
    },
    get ordering(): any {
      return self.choices[0]?.ordering
    },
    get correctChoices() {
      return self.choices.filter((c) => c.correct)
    },
    get answeredCorrectly() {
      if (self.answered) {
        if (self.question_type === 'Matching') {
          return isEqual(
            self.userMatching,
            this.pairs.map((p) => ({ left: p.left, right: p.right })),
          )
        } else if (self.question_type === 'Ordering') {
          return isEqual(
            self.userOrdering.map((o) => ({ text: o.text })),
            this.ordering.map((o) => ({ text: o.text })),
          )
        } else if (self.question_type === 'Open Text') {
          if (self.case_sensitive && self.userText) {
            const correct_choices = self.choices.filter((c) => c.correct).map((c) => c.text)
            return correct_choices.includes(self.userText)
          } else if (self.userText) {
            const correct_choices = self.choices
              .filter((c) => c.correct)
              .map((c) => c.text?.toLowerCase())
            return correct_choices.includes(self.userText.toLowerCase())
          } else {
            return false
          }
        } else {
          return isEmpty(
            xorWith(
              this.selected,
              self.choices.filter((c) => c.correct),
              isEqual,
            ),
          )
        }
      } else {
        return false
      }
    },
    get showChoiceFeedback() {
      return self.choices.some((c) => c.factoid)
    },
    get longChoices() {
      return (
        self.choices?.length === 4 ||
        self.choices?.some((choice: any) => choice?.text?.length >= 100)
      )
    },
    get longQuestion() {
      return self.text?.length >= 400
    },
    get rootStore(): any {
      return getRoot(self)
    },
  }))
  .actions((self) => ({
    next(fromFactoid) {
      const quiz = getParent(self, 2) as any
      if (quiz) {
        quiz.next(fromFactoid)
      }
    },
    continueFromFeedback() {
      self.setShowFeedback(false)
      this.next(true)
    },
    answer() {
      self.answered = true
      const { userStore } = self.rootStore

      if (self.question_type === 'Matching' && self.userMatching) {
        self.pairs.map((pair, idx) => {
          if (
            self.userMatching[idx].left === pair.left &&
            self.userMatching[idx].right === pair.right
          ) {
            pair.setMatchedCorrectly(true)
          } else {
            pair.setMatchedCorrectly(false)
          }
        })
      } else if (self.question_type === 'Ordering' && self.userOrdering) {
        self.ordering.map((ordering, idx) => {
          if (self.userOrdering[idx].text === ordering.text) {
            ordering.setOrderedCorrectly(true)
          } else {
            ordering.setOrderedCorrectly(false)
          }
        })
      }

      const alwaysShowFeedback = userStore?.user?.workspace_settings?.always_show_feedback
      const showConfirmationPopups = userStore?.user?.workspace_settings?.show_confirmation_popup

      if (showConfirmationPopups) {
        if (alwaysShowFeedback) {
          self.setShowFeedback(true)
        } else {
          if (self.answeredCorrectly) {
            self.setShowFeedback(false)
          } else {
            self.setShowFeedback(true)
          }
        }
      } else {
        if (alwaysShowFeedback) {
          if (self.choices.some((c) => c.factoid) || self?.factoid?.text) {
            self.setShowFeedback(true)
          } else {
            self.setShowFeedback(false)
          }
        } else {
          if (self.answeredCorrectly) {
            self.setShowFeedback(false)
          } else {
            if (self.choices.some((c) => c.factoid) || self?.factoid?.text) {
              self.setShowFeedback(true)
            } else {
              self.setShowFeedback(false)
            }
          }
        }
      }
      if (!self.showFeedback) {
        this.next(false)
      }
    },
  }))
